import { api } from "../api";

/**
 * API de Gestão de Documentos Específicos por Projeto
 * Consumir endpoints do backend implementado
 */

// ==================== DOCUMENTOS DE PROJETO ====================

/**
 * Obter visão geral dos documentos do projeto (slots)
 * @param {number} projectId - ID do projeto
 * @returns {Promise} - ProjectDocumentsOverviewDTO
 */
export const getProjectDocuments = async (projectId) => {
  const response = await api.get(`/api/projects/${projectId}/documents`);
  return response.data;
};

/**
 * Upload de novo documento
 * @param {number} projectId - ID do projeto
 * @param {number} documentTypeId - ID do tipo de documento
 * @param {File} file - Arquivo a ser enviado
 * @param {string} comments - Comentários opcionais
 * @param {function} onUploadProgress - Callback para progresso do upload
 * @returns {Promise} - ProjectDocumentResponseDTO
 */
export const uploadDocument = async (projectId, documentTypeId, file, comments, onUploadProgress) => {
  const formData = new FormData();
  formData.append("projectId", projectId);
  formData.append("documentTypeId", documentTypeId);
  formData.append("fileName", file.name); // Enviar nome do arquivo explicitamente
  formData.append("file", file);
  if (comments) formData.append("comments", comments);

  const response = await api.post(`/api/projects/${projectId}/documents`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (progressEvent) => {
      if (onUploadProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onUploadProgress(percentCompleted);
      }
    },
  });
  return response.data;
};

/**
 * Buscar documento específico por ID
 * @param {number} documentId - ID do documento
 * @returns {Promise} - ProjectDocumentResponseDTO
 */
export const getDocumentById = async (documentId) => {
  const response = await api.get(`/api/documents/${documentId}`);
  return response.data;
};

/**
 * Obter histórico de versões de um tipo de documento
 * @param {number} projectId - ID do projeto
 * @param {number} documentTypeId - ID do tipo de documento
 * @returns {Promise} - Array de ProjectDocumentResponseDTO
 */
export const getVersionHistory = async (projectId, documentTypeId) => {
  const response = await api.get(
    `/api/projects/${projectId}/documents/types/${documentTypeId}/history`
  );
  return response.data;
};

/**
 * Atualizar comentários de um documento
 * @param {number} documentId - ID do documento
 * @param {string} comments - Novos comentários
 * @returns {Promise} - ProjectDocumentResponseDTO
 */
export const updateDocument = async (documentId, comments) => {
  const response = await api.put(`/api/documents/${documentId}`, { comments });
  return response.data;
};

/**
 * Deletar documento
 * @param {number} documentId - ID do documento
 * @returns {Promise}
 */
export const deleteDocument = async (documentId) => {
  const response = await api.delete(`/api/documents/${documentId}`);
  return response.data;
};

/**
 * Obter URL de download do documento
 * @param {number} documentId - ID do documento
 * @returns {Promise} - { url: string }
 */
export const getDownloadUrl = async (documentId) => {
  const response = await api.get(`/api/documents/${documentId}/download`);
  return response.data;
};

/**
 * Obter o arquivo como Blob para visualização inline (preview em iframe).
 * Diferente de getDownloadUrl, esse endpoint devolve o binário direto com
 * Content-Disposition: inline, evitando download forçado pelo browser.
 * @param {number} documentId - ID do documento
 * @returns {Promise<Blob>}
 */
export const getInlineBlob = async (documentId) => {
  const response = await api.get(`/api/documents/${documentId}/inline`, {
    responseType: "blob",
  });
  return response.data;
};

/**
 * Download direto do arquivo
 * @param {number} documentId - ID do documento
 * @param {string} fileName - Nome do arquivo para salvar
 * @returns {Promise}
 */
export const downloadDocument = async (documentId, fileName) => {
  try {
    const response = await api.get(`/api/documents/${documentId}/download`, {
      responseType: "blob",
    });
    
    // Criar link temporário para download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  } catch (error) {
    console.error("Erro ao fazer download do documento:", error);
    throw error;
  }
};

/**
 * Enviar documento via WhatsApp
 * @param {number} documentId - ID do documento
 * @param {string} recipientPhone - Telefone do destinatário (formato: 5511999999999)
 * @param {string} recipientName - Nome do destinatário (opcional)
 * @returns {Promise} - Resultado do envio
 */
export const sendDocumentWhatsApp = async (documentId, recipientPhone, recipientName) => {
  const response = await api.post(`/api/documents/${documentId}/send`, {
    recipientPhone,
    recipientName,
  });
  return response.data;
};

/**
 * Envio em lote de documentos via WhatsApp
 * @param {number} projectId - ID do projeto
 * @param {number[]} documentIds - Array de IDs de documentos
 * @param {string} recipientPhone - Telefone do destinatário
 * @param {string} recipientName - Nome do destinatário (opcional)
 * @returns {Promise} - Resultado do envio em lote
 */
export const bulkSendDocuments = async (projectId, documentIds, recipientPhone, recipientName) => {
  const response = await api.post(`/api/projects/${projectId}/documents/bulk-send`, {
    documentIds,
    recipientPhone,
    recipientName,
  });
  return response.data;
};

